import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../AuthContext';
import api from '../api';

// ================== I18N QUICK MESSAGES ==================
const QUICK_MESSAGES_FR = ["Bien joué !", "Haha !", "Pas de chance...", "Dépêche-toi !", "T'es nul !", "Domino !", "Boom !!!"];
const QUICK_MESSAGES_EN = ["Well played !", "Haha !", "Bad luck...", "Hurry up !", "You're bad !", "Domino !", "Boom !!!"];
const QUICK_MESSAGES_ES = ["¡Bien jugado!", "¡Jaja!", "Mala suerte...", "¡Date prisa!", "¡Eres malo!", "¡Dominó!", "¡Boom !!!"];

const AVATAR_LIST = [
  { icon: '🦁', name: 'Lion' }, { icon: '🦓', name: 'Zèbre' }, { icon: '🐉', name: 'Dragon' }, { icon: '🪐', name: 'Jupiter' },
  { icon: '🐆', name: 'Panthère' }, { icon: '🦅', name: 'Aigle' }, { icon: '🐼', name: 'Panda' }, { icon: '🦊', name: 'Renard' },
  { icon: '🐸', name: 'Grenouille' }, { icon: '🦄', name: 'Licorne' }, { icon: '🐺', name: 'Loup' }, { icon: '🐻', name: 'Ours' }
];

const BET_OPTIONS = [500, 1000, 1500, 2000, 2500, 5000];

// ================== DECK / RULES ==================
const generateDeck = () => {
  const deck = [];
  for (let i = 0; i <= 6; i++) { for (let j = i; j <= 6; j++) deck.push({ v1: i, v2: j }); }
  return deck;
};
const shuffleArray = (array) => {
  const newArr = [...array];
  for (let i = newArr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
  }
  return newArr;
};

const evaluateHandSynergy = (hand) => {
  let score = 0;
  const counts = Array(7).fill(0);
  let doubleCount = 0;
  hand.forEach(tile => {
    counts[tile.v1]++; counts[tile.v2]++;
    if (tile.v1 === tile.v2) { doubleCount++; score += 30; }
    score -= (tile.v1 + tile.v2);
  });
  const uniqueSuits = counts.filter(c => c > 0).length;
  score += uniqueSuits * 20;
  return score;
};

const getEnds = (board) => {
  if (!board || board.length === 0) return { left: null, right: null };
  const first = board[0]; const last = board[board.length - 1];
  const leftEnd = first.reversed ? first.tile.v2 : first.tile.v1;
  const rightEnd = last.reversed ? last.tile.v1 : last.tile.v2;
  return { left: leftEnd, right: rightEnd };
};

const checkValidMove = (tile, left, right) => {
  if (left === null && right === null) return true;
  const canPlayLeft = (tile.v1 === left || tile.v2 === left);
  const canPlayRight = (tile.v1 === right || tile.v2 === right);
  return canPlayLeft || canPlayRight;
};

const hasAnyValidMove = (hand, board) => {
  if (board.length === 0) return true;
  const { left, right } = getEnds(board);
  return hand.some(tile => checkValidMove(tile, left, right));
};

// ================== ZIG-ZAG LAYOUT ==================
const useDominoLayout = (board) => {
  return useMemo(() => {
    if (!board || board.length === 0) return [];
    const L = 84, W = 42, MAX_HORIZ = 5, MAX_VERT = 3;
    let rootIdx = board.findIndex(b => b.isRoot);
    if (rootIdx === -1) rootIdx = 0;
    const layout = [];
    const getDims = (isDouble, dir) => {
      const isHorizontal = (dir === 'RIGHT' || dir === 'LEFT');
      if (isDouble) return { w: isHorizontal ? W : L, h: isHorizontal ? L : W, len: W };
      return { w: isHorizontal ? L : W, h: isHorizontal ? W : L, len: L };
    };
    const getRotation = (dir, isDouble, isRight) => {
      if (isRight) {
        if (!isDouble) { if (dir === 'RIGHT') return 0; if (dir === 'DOWN') return 90; if (dir === 'LEFT') return 180; if (dir === 'UP') return 270; }
        else { if (dir === 'RIGHT' || dir === 'LEFT') return 90; if (dir === 'DOWN' || dir === 'UP') return 0; }
      } else {
        if (!isDouble) { if (dir === 'LEFT') return 0; if (dir === 'UP') return 90; if (dir === 'RIGHT') return 180; if (dir === 'DOWN') return 270; }
        else { if (dir === 'LEFT' || dir === 'RIGHT') return 90; if (dir === 'UP' || dir === 'DOWN') return 0; }
      }
      return 0;
    };
    let cx = 0, cy = 0;
    let currentDir = 'RIGHT', lastHoriz = 'RIGHT', straightCount = 0;
    for (let i = rootIdx; i < board.length; i++) {
      const item = board[i];
      const displayTile = item.reversed ? { v1: item.tile.v2, v2: item.tile.v1 } : item.tile;
      const isDouble = displayTile.v1 === displayTile.v2;
      if (i === rootIdx) {
        layout[i] = { ...item, displayTile, isDouble, x: cx, y: cy, rot: isDouble ? 90 : 0 };
        straightCount++; continue;
      }
      const prev = layout[i - 1];
      let maxLimit = (currentDir === 'DOWN' || currentDir === 'UP') ? MAX_VERT : MAX_HORIZ;
      let turned = false;
      if (straightCount >= maxLimit && !isDouble && !prev.isDouble) {
        if (currentDir === 'RIGHT' || currentDir === 'LEFT') { lastHoriz = currentDir; currentDir = 'DOWN'; }
        else if (currentDir === 'DOWN') { currentDir = lastHoriz === 'RIGHT' ? 'LEFT' : 'RIGHT'; }
        straightCount = 0; turned = true;
      }
      const prevD = getDims(prev.isDouble, prev.dir || 'RIGHT');
      const currD = getDims(isDouble, currentDir);
      if (!turned) {
        const dist = (prevD.len / 2) + (currD.len / 2);
        if (currentDir === 'RIGHT') cx = prev.x + dist;
        if (currentDir === 'LEFT') cx = prev.x - dist;
        if (currentDir === 'DOWN') cy = prev.y + dist;
      } else {
        if (prev.dir === 'RIGHT' && currentDir === 'DOWN') { cx = prev.x + prevD.w / 2 - currD.w / 2; cy = prev.y + prevD.h / 2 + currD.h / 2; }
        if (prev.dir === 'DOWN' && currentDir === 'LEFT') { cx = prev.x - prevD.w / 2 - currD.w / 2; cy = prev.y + prevD.h / 2 - currD.h / 2; }
        if (prev.dir === 'LEFT' && currentDir === 'DOWN') { cx = prev.x - prevD.w / 2 + currD.w / 2; cy = prev.y + prevD.h / 2 + currD.h / 2; }
        if (prev.dir === 'DOWN' && currentDir === 'RIGHT') { cx = prev.x + prevD.w / 2 + currD.w / 2; cy = prev.y + prevD.h / 2 - currD.h / 2; }
      }
      layout[i] = { ...item, displayTile, isDouble, x: cx, y: cy, dir: currentDir, rot: getRotation(currentDir, isDouble, true) };
      straightCount++;
    }
    if (board.length > 0) {
      cx = layout[rootIdx].x; cy = layout[rootIdx].y;
      currentDir = 'LEFT'; lastHoriz = 'LEFT'; straightCount = 1;
      for (let i = rootIdx - 1; i >= 0; i--) {
        const item = board[i];
        const displayTile = item.reversed ? { v1: item.tile.v2, v2: item.tile.v1 } : item.tile;
        const isDouble = displayTile.v1 === displayTile.v2;
        const prev = layout[i + 1];
        let maxLimit = (currentDir === 'DOWN' || currentDir === 'UP') ? MAX_VERT : MAX_HORIZ;
        let turned = false;
        if (straightCount >= maxLimit && !isDouble && !prev.isDouble) {
          if (currentDir === 'LEFT' || currentDir === 'RIGHT') { lastHoriz = currentDir; currentDir = 'UP'; }
          else if (currentDir === 'UP') { currentDir = lastHoriz === 'LEFT' ? 'RIGHT' : 'LEFT'; }
          straightCount = 0; turned = true;
        }
        const prevD = getDims(prev.isDouble, prev.dir || 'LEFT');
        const currD = getDims(isDouble, currentDir);
        if (!turned) {
          const dist = (prevD.len / 2) + (currD.len / 2);
          if (currentDir === 'RIGHT') cx = prev.x + dist;
          if (currentDir === 'LEFT') cx = prev.x - dist;
          if (currentDir === 'UP') cy = prev.y - dist;
        } else {
          if (prev.dir === 'LEFT' && currentDir === 'UP') { cx = prev.x - prevD.w / 2 + currD.w / 2; cy = prev.y - prevD.h / 2 - currD.h / 2; }
          if (prev.dir === 'UP' && currentDir === 'RIGHT') { cx = prev.x + prevD.w / 2 + currD.w / 2; cy = prev.y - prevD.h / 2 + currD.h / 2; }
          if (prev.dir === 'RIGHT' && currentDir === 'UP') { cx = prev.x + prevD.w / 2 - currD.w / 2; cy = prev.y - prevD.h / 2 - currD.h / 2; }
          if (prev.dir === 'UP' && currentDir === 'LEFT') { cx = prev.x - prevD.w / 2 - currD.w / 2; cy = prev.y - prevD.h / 2 + currD.h / 2; }
        }
        layout[i] = { ...item, displayTile, isDouble, x: cx, y: cy, dir: currentDir, rot: getRotation(currentDir, isDouble, false) };
        straightCount++;
      }
    }
    if (layout.length > 0) {
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      layout.forEach(item => {
        if (item.x < minX) minX = item.x; if (item.x > maxX) maxX = item.x;
        if (item.y < minY) minY = item.y; if (item.y > maxY) maxY = item.y;
      });
      const offsetX = (minX + maxX) / 2; const offsetY = (minY + maxY) / 2;
      layout.forEach(item => { item.x -= offsetX; item.y -= offsetY; });
    }
    return layout;
  }, [board]);
};

// ================== DOMINO TILE ==================
const DominoTile = ({ tile, isBoard = false, hidden = false, style, onClick = null, className = "", theme = "classic", dominoStyle = "bevel" }) => {
  const getThemeClasses = (t) => {
    switch (t) {
      case 'red': return { bg: 'from-red-600 to-red-800', border: 'border-red-950', dots: 'bg-white', dotShadow: 'shadow-[inset_1px_1px_2px_rgba(0,0,0,0.6)]', line: 'border-red-950/80' };
      case 'blue': return { bg: 'from-blue-600 to-blue-800', border: 'border-blue-950', dots: 'bg-white', dotShadow: 'shadow-[inset_1px_1px_2px_rgba(0,0,0,0.6)]', line: 'border-blue-950/80' };
      case 'purple': return { bg: 'from-purple-600 to-purple-800', border: 'border-purple-950', dots: 'bg-white', dotShadow: 'shadow-[inset_1px_1px_2px_rgba(0,0,0,0.6)]', line: 'border-purple-950/80' };
      case 'black': return { bg: 'from-gray-900 to-black', border: 'border-black', dots: 'bg-blue-400', dotShadow: 'shadow-[inset_-1px_-1px_2px_rgba(255,255,255,0.4)]', line: 'border-gray-800/80' };
      default: return { bg: 'from-[#ffffff] to-[#e6e6e6]', border: 'border-gray-400', dots: 'bg-black', dotShadow: 'shadow-[inset_-1px_-1px_2px_rgba(255,255,255,0.7)]', line: 'border-gray-400/80' };
    }
  };
  const themeClasses = getThemeClasses(theme);
  const styleClasses = dominoStyle === 'bevel'
    ? { card: "shadow-[4px_6px_10px_rgba(0,0,0,0.55),inset_-3px_-3px_5px_rgba(0,0,0,0.2),inset_3px_3px_5px_rgba(255,255,255,0.8)] border-b-2 border-r rounded-[8px]", divPin: "w-2 h-2 bg-yellow-600" }
    : { card: "shadow-[1px_2px_4px_rgba(0,0,0,0.3)] border rounded-[4px]", divPin: "w-1.5 h-1.5 bg-gray-500" };

  if (hidden) {
    return (
      <div style={style} className={`flex items-center justify-center bg-gradient-to-br from-yellow-850 to-[#3a2800] rounded-[6px] border-2 border-yellow-600 shadow-[inset_0_0_8px_rgba(0,0,0,0.9),_1px_2px_3px_rgba(0,0,0,0.8)] ${isBoard ? 'w-[84px] h-[42px] absolute' : 'w-[46px] h-[92px] shrink-0'} ${className}`}>
        <div className="text-[#d4af37] text-xl font-bold opacity-30">D</div>
      </div>
    );
  }
  const renderDots = (value) => {
    const positions = { 0: [], 1: ['center'], 2: ['top-left', 'bottom-right'], 3: ['top-left', 'center', 'bottom-right'], 4: ['top-left', 'top-right', 'bottom-left', 'bottom-right'], 5: ['top-left', 'top-right', 'center', 'bottom-left', 'bottom-right'], 6: ['top-left', 'top-right', 'mid-left', 'mid-right', 'bottom-left', 'bottom-right'] };
    const posClasses = { 'top-left': 'top-1.5 left-1.5', 'top-right': 'top-1.5 right-1.5', 'mid-left': 'top-1/2 -translate-y-1/2 left-1.5', 'mid-right': 'top-1/2 -translate-y-1/2 right-1.5', 'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2', 'bottom-left': 'bottom-1.5 left-1.5', 'bottom-right': 'bottom-1.5 right-1.5' };
    return (positions[value] || []).map((pos, idx) => (
      <div key={idx} className={`absolute w-2.5 h-2.5 rounded-full ${themeClasses.dots} ${themeClasses.dotShadow} ${posClasses[pos]}`}></div>
    ));
  };
  const { v1, v2 } = tile;
  return (
    <div onClick={onClick} style={style} className={`flex bg-gradient-to-br ${themeClasses.bg} border ${themeClasses.border} cursor-pointer active:scale-95 transition-all duration-300 ease-out ${styleClasses.card} ${isBoard ? 'flex-row w-[84px] h-[42px] absolute' : 'flex-col w-[46px] h-[92px] shrink-0'} ${className}`}>
      <div className={`relative flex-1 ${isBoard ? 'border-r-2' : 'border-b-2'} ${themeClasses.line}`}>{renderDots(v1)}</div>
      <div className="relative flex-1">
        {renderDots(v2)}
        <div className={`absolute rounded-full shadow-inner ${styleClasses.divPin} ${isBoard ? '-left-[5px] top-1/2 -translate-y-1/2' : '-top-[5px] left-1/2 -translate-x-1/2'}`}></div>
      </div>
    </div>
  );
};

// ================== MAIN COMPONENT ==================
export default function DominoGame({ onClose }) {
  const { user, refresh } = useAuth();

  // ===== EXISTING USER STATES (unchanged) =====
  const [board, setBoard] = useState([]);
  const [playerHand, setPlayerHand] = useState([]);
  const [aiHand, setAiHand] = useState([]);
  const [boneyard, setBoneyard] = useState([]);
  const [turn, setTurn] = useState('none');
  const [status, setStatus] = useState('start');
  const [winner, setWinner] = useState(null);
  const [winReason, setWinReason] = useState('normal');
  const [finalScores, setFinalScores] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);
  const [zoom, setZoom] = useState(0.85);
  const [activeMessage, setActiveMessage] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [passCount, setPassCount] = useState(0);
  const [lang, setLang] = useState('FR');
  const [showSettings, setShowSettings] = useState(false);
  const [dominoTheme, setDominoTheme] = useState('classic');
  const [dominoStyle, setDominoStyle] = useState('bevel');
  const [globalVolume, setGlobalVolume] = useState(1.0);
  const [myAvatar, setMyAvatar] = useState(AVATAR_LIST[0]);
  const [isDistributing, setIsDistributing] = useState(false);
  const [showBetModal, setShowBetModal] = useState(false);
  const [selectedBet, setSelectedBet] = useState(500);
  const [gamePot, setGamePot] = useState(0);
  const [timeLeft, setTimeLeft] = useState(200);
  const [newPrivateRoomCode, setNewPrivateRoomCode] = useState(null);
  const [comingSoonFeature, setComingSoonFeature] = useState(null);
  const [showQuitConfirm, setShowQuitConfirm] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [showRoomTypeModal, setShowRoomTypeModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [createRoomType, setCreateRoomType] = useState('Public');
  const [createRoomBet, setCreateRoomBet] = useState(500);
  const [activeRooms, setActiveRooms] = useState([]);

  // ===== NEW PvP / MULTIPLAYER STATES =====
  const [gameMode, setGameMode] = useState('ai');           // 'ai' | 'pvp'
  const socketRef = useRef(null);
  const [pvpRoomId, setPvpRoomId] = useState(null);
  const [pvpOpponent, setPvpOpponent] = useState(null);     // { username, avatar }
  const [pvpMyUsername, setPvpMyUsername] = useState(null);
  const [aiMatchId, setAiMatchId] = useState(null);          // /api/game/match/start
  const [lastBet, setLastBet] = useState(500);               // for auto-rematch on tie
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);

  const showToast = useCallback((msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 4500);
  }, []);

  const boardLayout = useDominoLayout(board);

  // ===== BALANCE FROM AUTH (REAL-TIME, NO HARDCODE) =====
  const realBalance = Math.floor(Number(user?.walletBalance || 0));

  // ============================================================
  // SOCKET.IO — connects only when gameMode === 'pvp'
  // Uses REACT_APP_BACKEND_URL, no localhost in code.
  // ============================================================
  useEffect(() => {
    if (gameMode !== 'pvp') {
      if (socketRef.current) { try { socketRef.current.disconnect(); } catch (_) { /* noop */ } socketRef.current = null; }
      return;
    }
    const url = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('da_token');
    if (!url || !token) { showToast('⚠️ Connexion impossible : non authentifié'); setGameMode('ai'); return; }
    const s = io(url, {
      path: '/api/socket.io', auth: { token },
      transports: ['websocket', 'polling'], withCredentials: true,
      reconnection: true, reconnectionAttempts: Infinity, reconnectionDelay: 1000, timeout: 10000,
    });
    socketRef.current = s;

    s.on('connect', () => { /* connected */ });
    s.on('connect_error', (e) => { showToast(`❌ Socket: ${e?.message || 'erreur'}`); });

    s.on('lobby:state', (rooms) => {
      const dominoRooms = (rooms || []).filter(r => r.game === 'domino' && r.status === 'waiting').map(r => ({
        id: r.id, host: r.host_username, bet: r.bet, type: r.type, status: 'En attente',
        createdAt: (r.createdAt || Date.now() / 1000) * 1000,
      }));
      setActiveRooms(dominoRooms);
    });

    s.on('room:player_joined', () => { setWaitingForOpponent(false); });

    s.on('game:init', (init) => {
      // Server-side authoritative deal → my real hand, opponent's count
      setPlayerHand(init.hand || []);
      setAiHand(Array(init.opponent_hand_count || 7).fill({ v1: 0, v2: 0 }));
      setBoneyard(Array(init.boneyard_count || 14).fill({ v1: 0, v2: 0 }));
      setBoard([]);
      setPassCount(0);
      setPendingMove(null);
      setFinalScores(null);
      setWinner(null);
      setWinReason('normal');
      setStatus('playing');
      setPvpOpponent(init.opponent);
      setPvpMyUsername(init.my_username);
      setGamePot(init.pot_payout || 0);
      setTimeLeft(200);
      setIsDistributing(true);
      setNewPrivateRoomCode(null);
      setWaitingForOpponent(false);
      setShowRoomTypeModal(false);
      setShowJoinModal(false);
      setShowBetModal(false);
      try { playShuffleSound(); } catch (_) { /* noop */ }
      setTimeout(() => {
        setIsDistributing(false);
        setTurn(init.first_turn === init.my_username ? 'player' : 'ai');
      }, 2800);
    });

    s.on('game:move', (p) => {
      const m = p?.move || {};
      if (m.action === 'place') {
        setBoard(prev => {
          const nb = [...prev];
          if (nb.length === 0) nb.push({ tile: m.tile, reversed: false, isRoot: true });
          else if (m.side === 'left') nb.unshift({ tile: m.tile, reversed: !!m.reversed });
          else nb.push({ tile: m.tile, reversed: !!m.reversed });
          return nb;
        });
        setAiHand(prev => prev.length > 0 ? prev.slice(0, -1) : prev);
        setPassCount(0);
      } else if (m.action === 'draw') {
        setBoneyard(prev => prev.length > 0 ? prev.slice(1) : prev);
        setAiHand(prev => [...prev, { v1: 0, v2: 0 }]);
        return; // draw doesn't pass turn
      } else if (m.action === 'pass') {
        setPassCount(prev => prev + 1);
      }
      // Opponent finished their action → my turn now (if game still on)
      setTurn('player');
      setTimeLeft(200);
    });

    s.on('domino:hands_update', (p) => {
      if (typeof p?.boneyard_count === 'number') {
        setBoneyard(Array(p.boneyard_count).fill({ v1: 0, v2: 0 }));
      }
    });

    s.on('room:player_left', () => {
      showToast(lang === 'FR' ? "🚪 L'adversaire a quitté." : lang === 'EN' ? '🚪 Opponent left.' : '🚪 El oponente se fue.');
    });

    s.on('balance:update', () => { try { refresh(); } catch (_) { /* noop */ } });

    s.on('game:end', (p) => {
      // Backend payload: { winner: username|null, payout, pot, results, by }
      try { refresh(); } catch (_) { /* noop */ }
      setStatus('finished');
      const w = p?.winner;
      if (w && w === pvpMyUsername) { setWinner('player'); setWinReason(p?.reason || 'normal'); }
      else if (!w) { setWinner('tie'); setWinReason('blocage'); }
      else { setWinner('ai'); setWinReason(p?.reason || 'normal'); }
    });

    return () => {
      try { s.disconnect(); } catch (_) { /* noop */ }
      socketRef.current = null;
    };
  }, [gameMode]);

  // ============================================================
  // ROOM EXPIRY CLEANUP (UI only — backend has its own TTL)
  // ============================================================
  useEffect(() => {
    if (gameMode === 'pvp') return; // backend pushes lobby state authoritatively
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      setActiveRooms(prevRooms => prevRooms.filter(room => (now - (room.createdAt || now)) < 300000));
    }, 1000);
    return () => clearInterval(cleanupInterval);
  }, [gameMode]);

  const playShuffleSound = () => {
    const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2003/2003-preview.mp3');
    audio.playbackRate = 0.75; audio.volume = globalVolume; audio.play().catch(() => { });
  };
  const playDealSound = () => {
    const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3');
    audio.playbackRate = 1.8; audio.volume = Math.min(1, globalVolume * 1.5); audio.play().catch(() => { });
  };

  const texts = {
    FR: { balance: "ÉQUILIBRE", chooseAvatar: "Choisir un Avatar", playIa: "JOUER CONTRE L'IA", createRoom: "CRÉER UNE SALLE", joinRoom: "REJOINDRE UNE SALLE", availableMatches: "MATCHS DISPONIBLES", noMatches: "Aucune partie en attente.", btnCancel: "ANNULER", btnPlay: "JOUER", chooseBet: "CHOISIS TA MISE", potTotal: "Pot total :", commission: "Commission (5%) :", winnerGain: "Gain du vainqueur :", platVide: "Plateau vide. Jouez le premier domino !", winnerNormal: "🎉 Vous avez gagné !", winnerAi: "🤖 L'adversaire a gagné !", winPts: "Vos points:", aiPts: "Adv. points:", btnReturn: "RETOURNER AU LOBBY", btnRejouer: "REJOUER UNE PARTIE", blocage: "BLOCAGE", timeout: "TEMPS ÉCOULÉ", gameBlocked: "Jeu bloqué !", potLabel: "Pot Total", piocheLabel: "Pioche:", msgRapide: "Messages Rapides", roomSetup: "CONFIGURATION DU MATCH", roomType: "Type de Salle :", public: "Public (Dans le lobby)", private: "Privé (Par code)", copied: "Salle créée !", settingsTitle: "PARAMÈTRES", langLabel: "Langue / Language :", themeLabel: "Couleur des Dominos :", styleLabel: "Style de Bout (3D) :", volLabel: "Volume Sonore :", comingSoonTitle: "Bientôt Disponible !", comingSoonDesc: "Cette section sera disponible très bientôt pour vous faire gagner des cadeaux, des points, et bien plus encore !", customBetLabel: "Saisir un montant personnalisé :", minBetError: "La mise minimale est de 500 DOMIA !", quitText: "Quitter 🚪", quitConfirmDesc: "Si vous quittez le jeu, vous risquez de perdre la partie ainsi que toute la somme en DOMIA pariée. Êtes-vous sûr de vouloir quitter ?", maxBet: "Mise Max", btnCancelRoom: "ANNULER LA CRÉATION", joiningMsg: "Connexion à la salle ", publicCreated: "Salle publique créée avec succès !" },
    EN: { balance: "BALANCE", chooseAvatar: "Choose an Avatar", playIa: "PLAY VS IA", createRoom: "CREATE A ROOM", joinRoom: "JOIN A ROOM", availableMatches: "AVAILABLE MATCHES", noMatches: "No pending matches.", btnCancel: "CANCEL", btnPlay: "PLAY", chooseBet: "CHOOSE YOUR BET", potTotal: "Total Pot:", commission: "Commission (5%):", winnerGain: "Winner Payout:", platVide: "Board is empty. Play the first domino!", winnerNormal: "🎉 You have won!", winnerAi: "🤖 Opponent has won!", winPts: "Your points:", aiPts: "Opp. points:", btnReturn: "RETURN TO LOBBY", btnRejouer: "PLAY AGAIN", blocage: "BLOCKED", timeout: "TIMEOUT", gameBlocked: "Game blocked !", potLabel: "Total Pot", piocheLabel: "Boneyard:", msgRapide: "Quick Messages", roomSetup: "MATCH CONFIGURATION", roomType: "Room Type:", public: "Public (Listed in Lobby)", private: "Private (Via invite code)", copied: "Room created!", settingsTitle: "SETTINGS", langLabel: "Language / Langue :", themeLabel: "Domino Colors :", styleLabel: "Domino Style (3D) :", volLabel: "Sound Volume :", comingSoonTitle: "Coming Soon!", comingSoonDesc: "This section will be available very soon to let you earn rewards, points, and much more!", customBetLabel: "Enter custom bet amount:", minBetError: "Minimum bet amount is 500 DOMIA!", quitText: "Quit 🚪", quitConfirmDesc: "If you quit the game, you risk losing the game and all betted DOMIA. Are you sure you want to quit?", maxBet: "Max Bet", btnCancelRoom: "CANCEL ROOM CREATION", joiningMsg: "Joining room ", publicCreated: "Public room successfully created!" },
    ES: { balance: "SALDO", chooseAvatar: "Elige un Avatar", playIa: "JUGAR CONTRA IA", createRoom: "CREAR SALA", joinRoom: "UNIRSE A SALA", availableMatches: "PARTIDAS DISPONIBLES", noMatches: "No hay partidas pendientes.", btnCancel: "CANCELAR", btnPlay: "JUGAR", chooseBet: "ELIGE TU APUESTA", potTotal: "Bote total:", commission: "Comisión (5%):", winnerGain: "Ganancia:", platVide: "Tablero vacío. ¡Juega el premier dominó!", winnerNormal: "🎉 ¡Has ganado!", winnerAi: "🤖 ¡El oponente ha ganado!", winPts: "Tus puntos:", aiPts: "Op. puntos:", btnReturn: "VOLVER AL LOBBY", btnRejouer: "JUGAR DE NUEVO", blocage: "BLOQUEO", timeout: "TIEMPO AGOTADO", gameBlocked: "¡Juego bloqueado!", potLabel: "Bote Total", piocheLabel: "Fichas:", msgRapide: "Mensajes Rápidos", roomSetup: "CONFIGURACIÓN DEL JUEGO", roomType: "Tipo de Sala:", public: "Pública (En lista)", private: "Privada (Con código)", copied: "¡Sala creada!", settingsTitle: "AJUSTES", langLabel: "Idioma / Language:", themeLabel: "Color de Dominós:", styleLabel: "Estilo de Dominó (3D):", volLabel: "Volumen Sonido:", comingSoonTitle: "¡Próximamente!", comingSoonDesc: "¡Esta sección estará disponible muy pronto para que ganes regalos, puntos y mucho más!", customBetLabel: "Ingresar apuesta personalizada:", minBetError: "¡La apuesta mínima es 500 DOMIA!", quitText: "Salir 🚪", quitConfirmDesc: "Si sales del juego, corres el riesgo de perder la partida y todo el DOMIA apostado. ¿Estás seguro de que quieres salir?", maxBet: "Apuesta Max", btnCancelRoom: "CANCELAR CREACIÓN DE SALA", joiningMsg: "Uniéndose a la sala ", publicCreated: "¡Sala pública creada con éxito!" }
  }[lang];

  const getRemainingTimeStr = (createdAt) => {
    const elapsed = Date.now() - createdAt;
    const remainingMs = Math.max(0, 300000 - elapsed);
    const remainingSecs = Math.floor(remainingMs / 1000);
    const mins = Math.floor(remainingSecs / 60);
    const secs = remainingSecs % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // ===== TIMER =====
  useEffect(() => {
    if (status !== 'playing' || isDistributing) return;
    if (timeLeft <= 0) {
      setStatus('finished');
      setWinner(turn === 'player' ? 'ai' : 'player');
      setWinReason('timeout');
      setFinalScores(null);
      return;
    }
    const timerId = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearInterval(timerId);
  }, [timeLeft, status, isDistributing, turn]);

  // =========================================================================
  // START GAME — branches on gameMode
  //   - 'ai' : original 250-trial rigged deal (UNCHANGED) + /api/game/match/start
  //   - 'pvp': hands come from `game:init` event — we only emit room_create here
  // =========================================================================
  const startAiGame = async () => {
    if (selectedBet < 500) { showToast(texts.minBetError); return; }
    if (realBalance < selectedBet) { showToast('❌ Solde insuffisant'); return; }
    setShowBetModal(false);
    setLastBet(selectedBet);
    // Debit bet on backend (PvE)
    try {
      const r = await api.post('/game/match/start', { bet: selectedBet, game: 'domino_ai' });
      setAiMatchId(r.data?.match_id || null);
      try { refresh(); } catch (_) { /* noop */ }
    } catch (e) {
      showToast(`❌ ${e?.response?.data?.detail || 'Erreur start'}`);
      return;
    }
    const potTotal = selectedBet * 2;
    setGamePot(potTotal * 0.95);

    // === USER's ORIGINAL AI DEAL — 250 trials, omniscient advantage stacking ===
    let deck = generateDeck();
    let bestAiHand = [], bestPlayerHand = [], bestBoneyard = [];
    let maxAdvantage = -Infinity;
    for (let k = 0; k < 250; k++) {
      const tempDeck = shuffleArray(deck);
      const tempAi = tempDeck.slice(0, 7);
      const tempPlayer = tempDeck.slice(7, 14);
      const tempBoneyard = tempDeck.slice(14);
      let aiScore = evaluateHandSynergy(tempAi);
      let playerDoubles = 0, playerPoints = 0;
      tempPlayer.forEach(t => { if (t.v1 === t.v2) playerDoubles++; playerPoints += (t.v1 + t.v2); });
      const playerScore = playerPoints - (playerDoubles * 50);
      const advantage = aiScore - playerScore;
      if (advantage > maxAdvantage) {
        maxAdvantage = advantage;
        bestAiHand = [...tempAi]; bestPlayerHand = [...tempPlayer]; bestBoneyard = [...tempBoneyard];
      }
    }
    setPlayerHand(bestPlayerHand); setAiHand(bestAiHand); setBoneyard(bestBoneyard); setBoard([]);
    setStatus('playing'); setWinner(null); setWinReason('normal'); setFinalScores(null);
    setPassCount(0); setActiveMessage(null); setPendingMove(null); setTimeLeft(200);
    setTurn('none'); setIsDistributing(true);
    playShuffleSound();
    let dealCount = 0;
    const dealInterval = setInterval(() => { playDealSound(); dealCount++; if (dealCount >= 14) clearInterval(dealInterval); }, 130);
    setTimeout(() => { setIsDistributing(false); setTurn('player'); }, 2800);
  };

  const startGame = () => {
    if (gameMode === 'ai') return startAiGame();
    // PvP "play again" — backend already wires game:init when room starts
  };

  const showMessage = (text, sender) => {
    setActiveMessage({ text, sender, time: Date.now() });
    setTimeout(() => setActiveMessage(null), 3500);
  };
  const sendPlayerMessage = (text) => {
    showMessage(text, myAvatar.name); setIsChatOpen(false);
    if (gameMode === 'pvp' && socketRef.current) {
      socketRef.current.emit('chat_message', { msg: text }); // best-effort
    }
  };

  // ===== USER'S ORIGINAL AI OMNISCIENT EVALUATOR (UNCHANGED) =====
  const evaluateOmniscientMove = (tile, side, currentLeft, currentRight) => {
    let score = 0;
    let nextLeft = side === 'left' ? (tile.v1 === currentLeft ? tile.v2 : tile.v1) : currentLeft;
    let nextRight = side === 'right' ? (tile.v2 === currentRight ? tile.v1 : tile.v2) : currentRight;
    if (currentLeft === null && currentRight === null) { nextLeft = tile.v1; nextRight = tile.v2; }
    const aiRemainingHand = aiHand.filter(t => t !== tile);
    const playerCanPlayLeft = playerHand.filter(p => p.v1 === nextLeft || p.v2 === nextLeft);
    const playerCanPlayRight = playerHand.filter(p => p.v1 === nextRight || p.v2 === nextRight);
    const playerValidPlays = [...new Set([...playerCanPlayLeft, ...playerCanPlayRight])];
    const aiCanPlayLeft = aiRemainingHand.filter(a => a.v1 === nextLeft || a.v2 === nextLeft);
    const aiCanPlayRight = aiRemainingHand.filter(a => a.v1 === nextRight || a.v2 === nextRight);
    const aiNextPlays = [...new Set([...aiCanPlayLeft, ...aiCanPlayRight])];
    const isTotalBlock = playerValidPlays.length === 0 && boneyard.length === 0 && aiNextPlays.length === 0;
    if (isTotalBlock || aiRemainingHand.length === 0) {
      let playerPts = playerHand.reduce((sum, t) => sum + t.v1 + t.v2, 0);
      let aiPts = aiRemainingHand.reduce((sum, t) => sum + t.v1 + t.v2, 0);
      if (aiPts < playerPts) return 9999999;
      else return -9999999;
    }
    if (playerValidPlays.length === 0) {
      score += 50000;
      let drawCount = 0;
      for (let bTile of boneyard) {
        drawCount++;
        if (bTile.v1 === nextLeft || bTile.v2 === nextLeft || bTile.v1 === nextRight || bTile.v2 === nextRight) break;
      }
      score += (drawCount * 10000);
      if (drawCount === boneyard.length && boneyard.length > 0) {
        const lastBoneTile = boneyard[boneyard.length - 1];
        const lastTileMatch = lastBoneTile.v1 === nextLeft || lastBoneTile.v2 === nextLeft || lastBoneTile.v1 === nextRight || lastBoneTile.v2 === nextRight;
        if (!lastTileMatch) score += 50000;
      }
    } else {
      score -= (playerValidPlays.length * 5000);
      const playerHighest = Math.max(...playerValidPlays.map(t => t.v1 + t.v2));
      score -= (playerHighest * 200);
    }
    if (aiNextPlays.length === 0 && aiRemainingHand.length > 0) score -= 20000;
    else score += (aiNextPlays.length * 1000);
    if (tile.v1 === tile.v2) score += 1500;
    score += (tile.v1 + tile.v2) * 20;
    return score;
  };

  // ===== AI MOVE LOOP — only runs in AI mode =====
  useEffect(() => {
    if (gameMode !== 'ai') return;
    if (turn !== 'ai' || status !== 'playing' || isDistributing) return;
    const timer = setTimeout(() => {
      const { left, right } = getEnds(board);
      let bestMove = null, highestMoveScore = -Infinity;
      for (let i = 0; i < aiHand.length; i++) {
        const tile = aiHand[i];
        const canPlayLeft = (board.length === 0 || tile.v1 === left || tile.v2 === left);
        const canPlayRight = (board.length === 0 || tile.v1 === right || tile.v2 === right);
        if (canPlayLeft) {
          const score = evaluateOmniscientMove(tile, 'left', left, right);
          if (score > highestMoveScore) { highestMoveScore = score; bestMove = { tileIndex: i, side: 'left', reversed: (board.length > 0 && tile.v1 === left) }; }
        }
        if (canPlayRight) {
          const score = evaluateOmniscientMove(tile, 'right', left, right);
          if (score > highestMoveScore) { highestMoveScore = score; bestMove = { tileIndex: i, side: 'right', reversed: (board.length > 0 && tile.v2 === right) }; }
        }
      }
      if (bestMove !== null) {
        let newBoard = [...board];
        const tile = aiHand[bestMove.tileIndex];
        if (newBoard.length === 0) newBoard.push({ tile, reversed: false, isRoot: true });
        else if (bestMove.side === 'left') newBoard.unshift({ tile, reversed: bestMove.reversed });
        else newBoard.push({ tile, reversed: bestMove.reversed });
        finishMove('ai', newBoard, bestMove.tileIndex);
      } else {
        if (boneyard.length > 0) {
          const newAiHand = [...aiHand], newBoneyard = [...boneyard];
          newAiHand.push(newBoneyard.shift());
          setAiHand(newAiHand); setBoneyard(newBoneyard);
        } else {
          setPassCount(prev => prev + 1); setTurn('player');
          if (Math.random() > 0.5) showMessage(lang === 'EN' ? "I can't play, but I'll still block you!" : "Je ne peux pas jouer, mais je vais te bloquer !", "AI");
        }
      }
    }, 1100);
    return () => clearTimeout(timer);
  }, [turn, aiHand, board, boneyard, status, isDistributing, playerHand, gameMode]);

  // ===== BLOCKAGE DETECTION (AI mode only — PvP handled by backend) =====
  useEffect(() => {
    if (gameMode !== 'ai') return;
    if (passCount >= 2 && status === 'playing') {
      const playerPts = playerHand.reduce((sum, tile) => sum + tile.v1 + tile.v2, 0);
      const aiPts = aiHand.reduce((sum, tile) => sum + tile.v1 + tile.v2, 0);
      setStatus('finished'); setFinalScores({ player: playerPts, ai: aiPts }); setWinReason('blocage');
      if (playerPts < aiPts) setWinner('player');
      else if (aiPts < playerPts) setWinner('ai');
      else setWinner('tie');
    }
  }, [passCount, status, playerHand, aiHand, gameMode]);

  const finishMove = (who, newBoard, tileIndex) => {
    let newHand = who === 'player' ? [...playerHand] : [...aiHand];
    newHand.splice(tileIndex, 1);
    if (who === 'player') setPlayerHand(newHand); else setAiHand(newHand);
    setBoard(newBoard); setPassCount(0); setPendingMove(null);

    if (newHand.length === 0) {
      setStatus('finished'); setWinner(who); setWinReason('normal'); setFinalScores(null);
      if (gameMode === 'pvp' && socketRef.current) {
        const winName = who === 'player' ? pvpMyUsername : pvpOpponent?.username;
        socketRef.current.emit('game_end', { winner: winName, reason: 'normal' });
      }
      if (who === 'ai') showMessage(lang === 'EN' ? "I saw all your tiles, I beat you! 💥" : "J'ai vu toutes tes pièces, je te bats ! 💥", "AI");
      else showMessage(lang === 'EN' ? "I made a mistake, well played..." : "J'ai fait une erreur, bien joué...", "AI");
    } else {
      if (gameMode === 'ai' && who === 'ai') {
        if (!hasAnyValidMove(playerHand, newBoard)) {
          if (boneyard.length > 2) setTimeout(() => showMessage(lang === 'EN' ? "Better go cry in the boneyard! 😂" : "Vas-y, pioche dans le tas ! 😂", "AI"), 600);
          else if (boneyard.length === 0) setTimeout(() => showMessage(lang === 'EN' ? "You can't play! Pass your turn! 🤣" : "Tu ne peux pas jouer ! Passe ton tour ! 🤣", "AI"), 600);
        }
      }
      setTurn(who === 'player' ? 'ai' : 'player');
    }
  };

  const attemptPlayTile = (tileIndex) => {
    if (turn !== 'player' || isDistributing) return;
    const tile = playerHand[tileIndex];
    const { left, right } = getEnds(board);
    if (board.length === 0) return executePlayTile(tileIndex, 'left');
    const canPlayLeft = (tile.v1 === left || tile.v2 === left);
    const canPlayRight = (tile.v1 === right || tile.v2 === right);
    if (canPlayLeft && canPlayRight && left !== right) {
      setPendingMove(pendingMove === tileIndex ? null : tileIndex);
    } else if (canPlayLeft) executePlayTile(tileIndex, 'left');
    else if (canPlayRight) executePlayTile(tileIndex, 'right');
  };

  const executePlayTile = (tileIndex, side) => {
    const tile = playerHand[tileIndex];
    let newBoard = [...board];
    const { left, right } = getEnds(newBoard);
    let reversed = false;
    if (newBoard.length === 0) newBoard.push({ tile, reversed: false, isRoot: true });
    else if (side === 'left') { reversed = tile.v1 === left; newBoard.unshift({ tile, reversed }); }
    else { reversed = tile.v2 === right; newBoard.push({ tile, reversed }); }

    // PvP : broadcast move to backend BEFORE local finishMove (so backend authority validates)
    if (gameMode === 'pvp' && socketRef.current) {
      socketRef.current.emit('game_move', { action: 'place', tile, side, reversed });
    }
    finishMove('player', newBoard, tileIndex);
  };

  const handleDraw = () => {
    if (boneyard.length === 0) return;
    if (gameMode === 'pvp' && socketRef.current) {
      socketRef.current.emit('domino_draw', {}, (res) => {
        if (res?.ok && res?.tile) {
          setPlayerHand(prev => [...prev, res.tile]);
          setBoneyard(prev => Array(res.boneyard_count || prev.slice(1).length).fill({ v1: 0, v2: 0 }));
          // Also broadcast the "draw" action so opponent's UI shows the boneyard shrink (server auto-broadcasts hands_update)
        } else {
          showToast(`❌ ${res?.error || 'Pioche refusée'}`);
        }
      });
      return;
    }
    // AI mode
    const newHand = [...playerHand], newBoneyard = [...boneyard];
    newHand.push(newBoneyard.shift());
    setPlayerHand(newHand); setBoneyard(newBoneyard);
  };

  const handlePass = () => {
    if (boneyard.length > 0) return;
    if (gameMode === 'pvp' && socketRef.current) {
      socketRef.current.emit('game_move', { action: 'pass' });
    }
    setPassCount(prev => prev + 1); setTurn('ai');
  };

  // =========================================================================
  // ROOM CREATE / JOIN — Socket.IO PvP
  // =========================================================================
  const executeCreateRoom = () => {
    if (createRoomBet < 500) { showToast(texts.minBetError); return; }
    if (realBalance < createRoomBet) { showToast('❌ Solde insuffisant pour cette mise'); return; }
    setLastBet(createRoomBet);
    setGameMode('pvp'); // mounts socket
    setSelectedBet(createRoomBet);
    setWaitingForOpponent(true);
    // small delay to let the socket connect
    setTimeout(() => {
      const s = socketRef.current;
      if (!s) { showToast('❌ Socket non disponible'); setGameMode('ai'); return; }
      s.emit('room_create', { game: 'domino', bet: createRoomBet, type: createRoomType.toLowerCase(), avatar: myAvatar.icon }, (res) => {
        if (res?.ok) {
          setPvpRoomId(res.room_id);
          if (createRoomType === 'Private') setNewPrivateRoomCode(res.room_id);
          else showToast(`✅ ${texts.publicCreated} (${res.room_id})`);
          setShowRoomTypeModal(false);
        } else {
          showToast(`❌ ${res?.error || 'Création échouée'}`);
          setGameMode('ai'); setWaitingForOpponent(false);
        }
      });
    }, 500);
  };

  const handleCancelPrivateRoom = () => {
    if (newPrivateRoomCode && socketRef.current) {
      socketRef.current.emit('room_leave', { room_id: newPrivateRoomCode });
    }
    setNewPrivateRoomCode(null);
    setPvpRoomId(null);
    setWaitingForOpponent(false);
    setGameMode('ai');
    showToast(lang === 'FR' ? "Création annulée !" : lang === 'EN' ? "Creation canceled!" : "¡Creación cancelada!");
  };

  const joinExistingRoom = (roomId, bet) => {
    setLastBet(bet);
    setGameMode('pvp');
    setSelectedBet(bet);
    setTimeout(() => {
      const s = socketRef.current;
      if (!s) { showToast('❌ Socket non disponible'); setGameMode('ai'); return; }
      s.emit('room_join', { room_id: roomId.toUpperCase(), avatar: myAvatar.icon }, (res) => {
        if (res?.ok) {
          setPvpRoomId(roomId.toUpperCase());
          showToast(`${texts.joiningMsg}${roomId.toUpperCase()}...`);
          setShowJoinModal(false); setJoinCode('');
        } else {
          showToast(`❌ ${res?.error || 'Salle introuvable'}`);
          setGameMode('ai');
        }
      });
    }, 500);
  };

  const handleJoinSubmit = () => {
    if (!joinCode) return;
    joinExistingRoom(joinCode, selectedBet || 500);
  };

  // =========================================================================
  // SETTLE AI MATCH ON FINISH + AUTO-REMATCH ON TIE
  // =========================================================================
  useEffect(() => {
    if (status !== 'finished') return;
    // AI mode: settle bet on backend
    if (gameMode === 'ai' && aiMatchId) {
      const result = winner === 'player' ? 'win' : (winner === 'tie' ? 'tie' : 'loss');
      api.post('/game/match/settle', { match_id: aiMatchId, result }).then(() => {
        try { refresh(); } catch (_) { /* noop */ }
      }).catch(() => { /* noop */ });
      setAiMatchId(null);
    }
    // Auto-rematch on tie/draw (per user request)
    if (winner === 'tie' && status === 'finished') {
      const t = setTimeout(() => {
        if (gameMode === 'ai') {
          setStatus('start'); setSelectedBet(lastBet);
          setTimeout(() => startAiGame(), 100);
        }
        // PvP tie auto-rematch: backend will re-init via new room_create flow — for now go back to lobby
      }, 4000);
      return () => clearTimeout(t);
    }
  }, [status, winner, gameMode]);

  // =========================================================================
  // RENDER — strictly identical UI to user's original, with PvP awareness
  // =========================================================================

  // ----- LOBBY -----
  if (status === 'start') {
    return (
      <div className="min-h-[100dvh] bg-[#04170b] relative overflow-hidden flex flex-col font-sans select-none pb-32" data-testid="domino-lobby">
        <style>{`
           @keyframes scanline { 0% { transform: translateX(-150%) skewX(-20deg); } 100% { transform: translateX(500%) skewX(-20deg); } }
           .animate-scan { animation: scanline 3s infinite linear; }
           @keyframes floatSlow { 0%, 100% { transform: translateY(0) rotate(-15deg); } 50% { transform: translateY(-20px) rotate(-10deg); } }
           .animate-float-1 { animation: floatSlow 4s ease-in-out infinite; }
           @keyframes floatSlowRev { 0%, 100% { transform: translateY(0) rotate(25deg) scale(1.2); } 50% { transform: translateY(15px) rotate(20deg) scale(1.2); } }
           .animate-float-2 { animation: floatSlowRev 5s ease-in-out infinite; }
           @keyframes floatSlowSide { 0%, 100% { transform: translateY(0) rotate(70deg) scale(0.8); } 50% { transform: translateY(-15px) rotate(75deg) scale(0.8); } }
           .animate-float-3 { animation: floatSlowSide 6s ease-in-out infinite; }
           @keyframes dropInToast { 0% { transform: translate(-50%, -50px); opacity: 0; } 100% { transform: translate(-50%, 0); opacity: 1; } }
           .animate-dropInToast { animation: dropInToast 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
           @keyframes dropIn { 0% { transform: translateY(-30px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
           .animate-dropIn { animation: dropIn 0.4s ease-out forwards; }
           @keyframes slideUp { 0% { transform: translateY(40px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
           .animate-slideUp { animation: slideUp 0.5s ease-out forwards; }
           @keyframes fadeIn { 0% { opacity: 0; } 100% { opacity: 1; } }
           .animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
           @keyframes fadeInOut { 0%,100% { opacity: 0; } 50% { opacity: 1; } }
           .animate-fadeInOut { animation: fadeInOut 2s ease-in-out infinite; }
           .no-scrollbar::-webkit-scrollbar { display: none; }
           .no-scrollbar { scrollbar-width: none; }
        `}</style>

        {toastMsg && (
          <div className="fixed top-10 left-1/2 -translate-x-1/2 z-[100] bg-[#0a1a0f] border-2 border-yellow-500 text-yellow-400 px-6 py-4 rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.8)] font-black text-sm sm:text-base animate-dropInToast text-center min-w-[280px] border-b-4 border-b-yellow-600" data-testid="domino-toast">
            {toastMsg}
          </div>
        )}

        <div className="absolute inset-0 bg-[#04170b]"></div>
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.55) 100%)' }}></div>
        <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-black/50 to-transparent"></div>

        <div className="absolute top-32 left-10 opacity-75 animate-float-1 pointer-events-none drop-shadow-[0_20px_20px_rgba(0,0,0,0.6)]">
          <DominoTile tile={{ v1: 6, v2: 6 }} isBoard={false} className="scale-[1.2]" theme={dominoTheme} dominoStyle={dominoStyle} />
        </div>
        <div className="absolute top-48 -right-10 opacity-75 animate-float-2 pointer-events-none drop-shadow-[0_20px_20px_rgba(0,0,0,0.7)]">
          <DominoTile tile={{ v1: 5, v2: 2 }} isBoard={false} theme={dominoTheme} dominoStyle={dominoStyle} />
        </div>
        <div className="absolute bottom-32 left-1/4 opacity-55 animate-float-3 pointer-events-none drop-shadow-[0_15px_15px_rgba(0,0,0,0.5)]">
          <DominoTile tile={{ v1: 4, v2: 0 }} isBoard={false} theme={dominoTheme} dominoStyle={dominoStyle} />
        </div>

        <div className="relative z-10 flex justify-between items-start p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="absolute -left-2 top-2 text-yellow-400 text-2xl px-2" data-testid="domino-back-platform">←</button>
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-yellow-300 to-yellow-600 rounded-xl p-[2px] shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all duration-300 ml-6">
              <div className="w-full h-full bg-[#0a1a0f] rounded-[10px] flex items-center justify-center text-4xl sm:text-5xl animate-pulse">{myAvatar.icon}</div>
            </div>
            <div className="flex flex-col">
              <span className="text-[#d4af37] font-black text-xl sm:text-2xl tracking-widest leading-none drop-shadow-md">DOMINO</span>
              <span className="text-[#e6c86a] font-black text-xl sm:text-2xl tracking-widest leading-none drop-shadow-md">ARENA</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="flex items-center bg-black/60 border border-yellow-600/50 rounded-full pr-1 pl-3 py-1 shadow-lg backdrop-blur-md" data-testid="domino-balance">
              <div className="flex flex-col mr-3">
                <span className="text-[8px] sm:text-[10px] text-yellow-500/70 font-bold uppercase tracking-wider text-right leading-none mb-1">DOMIA</span>
                <span className="text-white font-bold text-sm sm:text-base leading-none text-right">{realBalance.toLocaleString()}</span>
              </div>
              <button onClick={onClose} className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white font-black shadow-md hover:scale-105 transition-transform">+</button>
            </div>
            <button onClick={() => setShowSettings(!showSettings)} className="w-10 h-10 sm:w-12 sm:h-12 bg-black/60 border border-yellow-600/50 rounded-full flex items-center justify-center text-xl sm:text-2xl shadow-lg backdrop-blur-md hover:bg-yellow-900/40 transition-colors">⚙️</button>
          </div>
        </div>

        <div className="relative z-10 mt-1 flex flex-col items-center max-w-full px-2">
          <span className="text-yellow-600/80 text-xs font-bold uppercase tracking-widest mb-1.5">{texts.chooseAvatar}</span>
          <div className="flex gap-2.5 bg-black/40 p-2 rounded-full border border-yellow-700/30 backdrop-blur-sm overflow-x-auto no-scrollbar max-w-full">
            {AVATAR_LIST.map((avatar, idx) => (
              <div key={idx} onClick={() => setMyAvatar(avatar)} className={`cursor-pointer w-10 h-10 shrink-0 flex items-center justify-center rounded-full text-2xl transition-all duration-300 ${myAvatar.name === avatar.name ? 'bg-gradient-to-b from-yellow-400 to-yellow-600 border border-white shadow-[0_0_15px_rgba(212,175,55,0.8)] scale-110' : 'bg-gray-800 border border-gray-600 opacity-60 hover:opacity-100'}`}>
                {avatar.icon}
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex-grow flex flex-col items-center justify-center w-full max-w-md mx-auto px-6 gap-4 py-4 mb-24">
          <button onClick={() => { setGameMode('ai'); setShowBetModal(true); }} data-testid="domino-play-ai-btn" className="relative w-full group overflow-hidden rounded-xl border-2 border-[#e3ca71] shadow-[0_0_15px_rgba(34,197,94,0.3)] bg-gradient-to-b from-[#1b5e20] to-[#0a3311] hover:scale-105 active:scale-95 transition-all duration-300">
            <div className="absolute inset-y-0 left-0 w-[30%] bg-gradient-to-r from-transparent via-white/50 to-transparent skew-x-[-20deg] animate-scan"></div>
            <div className="relative z-10 px-5 py-4 sm:py-5 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🤖</span>
                <span className="text-white font-black text-lg sm:text-xl tracking-wider text-left block leading-tight">{texts.playIa}</span>
              </div>
              <span className="text-xl text-yellow-400">➔</span>
            </div>
          </button>

          <button onClick={() => showToast(lang === 'FR' ? '🚧 Multijoueur en préparation. Patience !' : lang === 'EN' ? '🚧 Multiplayer coming soon. Stay tuned!' : '🚧 ¡Multijugador en preparación!')} data-testid="domino-create-room-btn" className="relative w-full group overflow-hidden rounded-xl border-2 border-[#e3ca71] shadow-[0_0_15px_rgba(59,130,246,0.3)] bg-gradient-to-b from-[#1e3a8a]/60 to-[#0f172a]/60 opacity-70 cursor-not-allowed transition-all duration-300">
            <div className="relative z-10 px-5 py-4 sm:py-5 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <span className="text-2xl text-blue-300">👥</span>
                <span className="text-white font-black text-lg sm:text-xl tracking-wider text-left block leading-tight">{texts.createRoom}</span>
              </div>
              <span className="text-[10px] font-black bg-yellow-500 text-black px-2 py-1 rounded uppercase">{lang === 'FR' ? 'Bientôt' : lang === 'EN' ? 'Soon' : 'Pronto'}</span>
            </div>
          </button>

          <button onClick={() => showToast(lang === 'FR' ? '🚧 Multijoueur en préparation. Patience !' : lang === 'EN' ? '🚧 Multiplayer coming soon. Stay tuned!' : '🚧 ¡Multijugador en preparación!')} data-testid="domino-join-room-btn" className="relative w-full group overflow-hidden rounded-xl border-2 border-[#e3ca71] shadow-[0_0_15px_rgba(168,85,247,0.3)] bg-gradient-to-b from-[#6b21a8]/60 to-[#3b0764]/60 opacity-70 cursor-not-allowed transition-all duration-300">
            <div className="relative z-10 px-5 py-4 sm:py-5 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <span className="text-2xl text-purple-300">🔑</span>
                <span className="text-white font-black text-lg sm:text-xl tracking-wider text-left block leading-tight">{texts.joinRoom}</span>
              </div>
              <span className="text-[10px] font-black bg-yellow-500 text-black px-2 py-1 rounded uppercase">{lang === 'FR' ? 'Bientôt' : lang === 'EN' ? 'Soon' : 'Pronto'}</span>
            </div>
          </button>

          <div className="w-full mt-1 bg-black/60 rounded-xl border border-yellow-700/30 p-3 shadow-inner">
            <div className="flex justify-between items-center border-b border-yellow-700/20 pb-1.5 mb-2">
              <span className="text-[#d4af37] text-xs font-black tracking-widest uppercase">{texts.availableMatches}</span>
              <span className="text-green-500 text-[10px] animate-pulse">● {gameMode === 'pvp' ? 'Live' : 'En attente'}</span>
            </div>
            <div className="max-h-[120px] overflow-y-auto no-scrollbar flex flex-col gap-2">
              {activeRooms.length === 0 ? (
                <p className="text-gray-500 text-xs text-center py-4">{texts.noMatches}</p>
              ) : (
                activeRooms.map((room, index) => (
                  <div key={room.id || index} className="flex justify-between items-center bg-[#0d2a1a] p-2 rounded border border-green-900/40">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-1.5">
                        <span className="text-white font-bold text-xs">{room.id}</span>
                        <span className="text-[10px] text-red-400 font-mono bg-red-950/40 px-1 py-0.5 rounded border border-red-900/20">⏳ {getRemainingTimeStr(room.createdAt)}</span>
                      </div>
                      <span className="text-gray-400 text-[10px]">{lang === 'FR' ? 'Hôte' : lang === 'EN' ? 'Host' : 'Anfitrión'}: {room.host}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[#d4af37] text-xs font-mono font-bold">{room.bet} DOMIA</span>
                      {room.type === 'Private' || room.type === 'private' ? (
                        <button onClick={() => { setJoinCode(''); setShowJoinModal(true); }} className="bg-gray-800 text-gray-400 font-black text-[10px] px-3 py-1.5 rounded uppercase border border-gray-600 flex items-center gap-1 hover:bg-gray-700 transition-colors">
                          🔒 {lang === 'FR' ? 'Code Requis' : lang === 'EN' ? 'Code Req.' : 'Cód. Req.'}
                        </button>
                      ) : (
                        <button onClick={() => joinExistingRoom(room.id, room.bet)} className="bg-yellow-600 hover:bg-yellow-500 text-black font-black text-[10px] px-3 py-1.5 rounded uppercase transition-colors" data-testid={`domino-join-${room.id}`}>
                          {lang === 'FR' ? 'Rejoindre' : lang === 'EN' ? 'Join' : 'Unirse'}
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* WAITING MODAL */}
        {waitingForOpponent && (
          <div className="fixed inset-0 z-[70] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-[#0f212e] p-6 rounded-2xl border-2 border-yellow-500 text-center w-full max-w-sm">
              <div className="text-5xl animate-pulse mb-3">⏳</div>
              <h2 className="text-lg font-black text-yellow-500 mb-2 uppercase">{lang === 'FR' ? 'En attente d\'un joueur...' : lang === 'EN' ? 'Waiting for opponent...' : 'Esperando oponente...'}</h2>
              <p className="text-gray-300 text-xs mb-4">{lang === 'FR' ? 'Partagez le code de salle ou attendez qu\'un joueur rejoigne via le lobby.' : 'Share your room code or wait for a player to join.'}</p>
              {pvpRoomId && <div className="bg-black p-3 rounded-lg text-xl font-mono text-white mb-4 tracking-widest border border-gray-600 select-all">{pvpRoomId}</div>}
              <button onClick={handleCancelPrivateRoom} className="w-full bg-red-600 hover:bg-red-500 text-white font-bold py-2 rounded text-xs uppercase">{texts.btnCancel}</button>
            </div>
          </div>
        )}

        {/* PRIVATE ROOM CODE MODAL */}
        {newPrivateRoomCode && !waitingForOpponent && (
          <div className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto pt-6">
            <div className="bg-[#0f212e] p-5 rounded-2xl border-2 border-yellow-500 text-center w-full max-w-sm shadow-[0_0_50px_rgba(212,175,55,0.6)] animate-dropIn my-auto">
              <div className="text-3xl mb-2">🔒</div>
              <h2 className="text-lg font-black text-yellow-500 mb-2 uppercase">{lang === 'FR' ? 'SALLE PRIVÉE !' : lang === 'EN' ? 'PRIVATE ROOM!' : '¡SALA PRIVADA!'}</h2>
              <p className="text-gray-300 mb-4 text-xs">{lang === 'FR' ? "Voici le code de votre salle. Partagez-le pour jouer." : lang === 'EN' ? "Here is your room code. Share it to play together." : "Aquí está el código de tu sala. Compártelo para jugar."}</p>
              <div className="bg-black p-3 rounded-lg text-2xl font-mono text-white mb-5 tracking-widest border border-gray-600 shadow-inner select-all">{newPrivateRoomCode}</div>
              <button onClick={() => { try { navigator.clipboard.writeText(newPrivateRoomCode); } catch (e) { /* noop */ } showToast(lang === 'FR' ? "Code copié !" : "Code copied!"); }} className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-black py-3 rounded-lg shadow-lg hover:scale-105 transition-transform text-xs uppercase mb-2">{lang === 'FR' ? 'COPIER ET FERMER' : 'COPY AND CLOSE'}</button>
              <button onClick={handleCancelPrivateRoom} className="w-full bg-transparent border border-red-500/50 hover:bg-red-950/40 text-red-400 font-bold py-2 rounded-lg transition-colors text-xs uppercase">{texts.btnCancelRoom}</button>
            </div>
          </div>
        )}

        {comingSoonFeature && (
          <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto pt-6">
            <div className="bg-[#0f212e] p-6 rounded-2xl border-2 border-yellow-500 text-center w-full max-w-sm shadow-[0_0_35px_rgba(212,175,55,0.5)] animate-dropIn my-auto">
              <div className="text-5xl mb-3">✨</div>
              <h2 className="text-xl font-black text-yellow-500 mb-3 uppercase tracking-wider">{texts.comingSoonTitle}</h2>
              <p className="text-gray-200 text-xs mb-5 leading-relaxed">{texts.comingSoonDesc}</p>
              <button onClick={() => setComingSoonFeature(null)} className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-black py-2.5 rounded-lg hover:scale-105">OK</button>
            </div>
          </div>
        )}

        {showBetModal && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto pt-6">
            <div className="bg-[#0f212e] p-5 rounded-2xl border-2 border-[#d4af37] w-full max-w-sm shadow-[0_0_50px_rgba(212,175,55,0.4)] animate-dropIn my-auto">
              <h2 className="text-lg font-black text-[#d4af37] text-center mb-1 tracking-wider uppercase">{texts.playIa}</h2>
              <h3 className="text-[10px] font-bold text-gray-400 text-center mb-3 tracking-widest">{texts.chooseBet}</h3>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {BET_OPTIONS.map((betValue, idx) => (
                  <button key={idx} onClick={() => setSelectedBet(betValue)} className={`py-1.5 rounded-lg font-bold text-xs border transition-all ${selectedBet === betValue ? 'bg-gradient-to-b from-yellow-400 to-yellow-600 border-yellow-200 text-black shadow-[0_0_10px_rgba(212,175,55,0.6)] scale-105' : 'bg-[#152a21] border-[#d4af37]/50 text-[#d4af37] hover:bg-[#1f3f32]'}`}>
                    {betValue === 5000 ? texts.maxBet : `${betValue}`}
                  </button>
                ))}
              </div>
              <div className="mb-3 bg-black/40 p-2 rounded-lg border border-yellow-500/20">
                <label className="text-gray-300 text-[10px] font-bold block mb-1 text-center">{texts.customBetLabel}</label>
                <input type="number" min="500" step="100" placeholder="Min. 500" value={selectedBet || ''} onChange={(e) => setSelectedBet(parseInt(e.target.value) || 0)} className="w-full bg-[#070f07] border border-yellow-500/40 text-yellow-400 font-bold text-center text-xs p-1.5 rounded focus:outline-none" />
              </div>
              <div className="bg-black/40 p-3 rounded-lg border border-gray-600 mb-3 text-xs">
                <div className="flex justify-between text-gray-300 mb-1"><span>{texts.potTotal}</span><span className="font-bold text-yellow-500">{selectedBet * 2} DOMIA</span></div>
                <div className="flex justify-between text-gray-400 mb-1 border-b border-gray-600 pb-1"><span>{texts.commission}</span><span className="font-bold text-red-400">{selectedBet === 0 ? "0" : (selectedBet * 2 * 0.05).toFixed(0)}</span></div>
                <div className="flex justify-between text-[#d4af37] font-bold"><span>{texts.winnerGain}</span><span className="text-green-400">{selectedBet === 0 ? "0" : (selectedBet * 2 * 0.95).toFixed(0)}</span></div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowBetModal(false)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 rounded text-xs">{texts.btnCancel}</button>
                <button onClick={startAiGame} disabled={selectedBet < 500} className={`flex-1 font-bold py-2 rounded text-xs shadow ${selectedBet < 500 ? 'bg-gray-800 text-gray-500 cursor-not-allowed' : 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-black'}`} data-testid="domino-start-ai">{texts.btnPlay}</button>
              </div>
            </div>
          </div>
        )}

        {showRoomTypeModal && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto pt-6">
            <div className="bg-[#0f212e] p-5 rounded-2xl border-2 border-[#d4af37] w-full max-w-sm shadow-[0_0_50px_rgba(212,175,55,0.4)] animate-dropIn my-auto">
              <h2 className="text-lg font-black text-[#d4af37] text-center mb-3 tracking-wider uppercase">{texts.roomSetup}</h2>
              <div className="mb-3">
                <label className="text-gray-300 text-[10px] font-bold block mb-1">{texts.roomType}</label>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setCreateRoomType('Public')} className={`py-1.5 rounded font-bold text-xs border transition-all ${createRoomType === 'Public' ? 'bg-[#d4af37] text-black border-white' : 'bg-gray-800 text-gray-400 border-gray-600'}`}>🌐 PUBLIC</button>
                  <button onClick={() => setCreateRoomType('Private')} className={`py-1.5 rounded font-bold text-xs border transition-all ${createRoomType === 'Private' ? 'bg-[#d4af37] text-black border-white' : 'bg-gray-800 text-gray-400 border-gray-600'}`}>🔒 PRIVATE</button>
                </div>
              </div>
              <div className="mb-3">
                <label className="text-gray-300 text-[10px] font-bold block mb-1">{texts.chooseBet}</label>
                <div className="grid grid-cols-3 gap-1.5">
                  {BET_OPTIONS.map((betValue, idx) => (
                    <button key={idx} onClick={() => setCreateRoomBet(betValue)} className={`py-1 rounded font-bold text-xs border transition-all ${createRoomBet === betValue ? 'bg-gradient-to-b from-yellow-400 to-yellow-600 border-yellow-200 text-black shadow-md scale-105' : 'bg-[#152a21] border-[#d4af37]/30 text-[#d4af37] hover:bg-[#1f3f32]'}`}>{betValue}</button>
                  ))}
                </div>
              </div>
              <div className="mb-3 bg-black/40 p-2 rounded border border-yellow-500/20">
                <label className="text-gray-300 text-[10px] font-bold block mb-1 text-center">{texts.customBetLabel}</label>
                <input type="number" min="500" step="100" placeholder="Min. 500" value={createRoomBet || ''} onChange={(e) => setCreateRoomBet(parseInt(e.target.value) || 0)} className="w-full bg-[#070f07] border border-yellow-500/40 text-yellow-400 font-bold text-center text-xs p-1 rounded" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowRoomTypeModal(false)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 rounded text-xs">{texts.btnCancel}</button>
                <button onClick={executeCreateRoom} disabled={createRoomBet < 500} className={`flex-1 font-bold py-2 rounded text-xs shadow-lg transition-transform ${createRoomBet < 500 ? 'bg-gray-800 text-gray-500 cursor-not-allowed' : 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-black hover:scale-105'}`} data-testid="domino-create-room-confirm">{texts.btnPlay}</button>
              </div>
            </div>
          </div>
        )}

        {showJoinModal && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto pt-6">
            <div className="bg-[#0f212e] p-5 rounded-2xl border-2 border-[#d4af37] w-full max-w-sm shadow-[0_0_50px_rgba(212,175,55,0.4)] animate-dropIn my-auto">
              <h2 className="text-lg font-black text-[#d4af37] text-center mb-3 tracking-wider uppercase">{texts.joinRoom}</h2>
              <input type="text" value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="Ex: 123456" className="w-full bg-[#0a1a14] border border-[#d4af37] text-white p-2 rounded-lg text-center font-bold mb-4 uppercase focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm" data-testid="domino-join-code-input" />
              <div className="flex gap-2">
                <button onClick={() => setShowJoinModal(false)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 rounded text-xs">{texts.btnCancel}</button>
                <button onClick={handleJoinSubmit} className="flex-1 bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-bold py-2 rounded text-xs shadow-lg hover:scale-105" data-testid="domino-join-confirm">{texts.btnPlay}</button>
              </div>
            </div>
          </div>
        )}

        {showSettings && (
          <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto pt-10">
            <div className="bg-[#0f212e] p-5 rounded-2xl border-2 border-[#d4af37] w-full max-w-sm shadow-[0_0_50px_rgba(212,175,55,0.4)] animate-dropIn my-auto pb-6">
              <h2 className="text-lg font-black text-[#d4af37] text-center mb-3 tracking-wider uppercase">{texts.settingsTitle}</h2>
              <div className="mb-3">
                <label className="text-gray-300 text-[10px] font-bold block mb-1 text-center">{texts.langLabel}</label>
                <div className="grid grid-cols-3 gap-2">
                  <button onClick={() => setLang('FR')} className={`py-1 rounded font-black text-xs ${lang === 'FR' ? 'bg-[#d4af37] text-black border border-white' : 'bg-gray-800 text-gray-400'}`}>FR</button>
                  <button onClick={() => setLang('EN')} className={`py-1 rounded font-black text-xs ${lang === 'EN' ? 'bg-[#d4af37] text-black border border-white' : 'bg-gray-800 text-gray-400'}`}>EN</button>
                  <button onClick={() => setLang('ES')} className={`py-1 rounded font-black text-xs ${lang === 'ES' ? 'bg-[#d4af37] text-black border border-white' : 'bg-gray-800 text-gray-400'}`}>ES</button>
                </div>
              </div>
              <div className="mb-3 border-t border-gray-700 pt-2">
                <label className="text-gray-300 text-[10px] font-bold flex justify-between mb-1"><span>{texts.volLabel}</span><span className="text-[#d4af37]">{Math.round(globalVolume * 100)}%</span></label>
                <input type="range" min="0" max="1" step="0.1" value={globalVolume} onChange={(e) => setGlobalVolume(parseFloat(e.target.value))} className="w-full accent-yellow-500 cursor-pointer" />
              </div>
              <div className="mb-3 border-t border-gray-700 pt-2">
                <label className="text-gray-300 text-[10px] font-bold block mb-1 text-center">{texts.themeLabel}</label>
                <div className="flex justify-center gap-2">
                  <div onClick={() => setDominoTheme('classic')} className={`w-6 h-10 rounded cursor-pointer border bg-white ${dominoTheme === 'classic' ? 'border-yellow-400 scale-105' : 'border-gray-500'}`}></div>
                  <div onClick={() => setDominoTheme('red')} className={`w-6 h-10 rounded cursor-pointer border bg-red-700 ${dominoTheme === 'red' ? 'border-yellow-400 scale-105' : 'border-gray-500'}`}></div>
                  <div onClick={() => setDominoTheme('blue')} className={`w-6 h-10 rounded cursor-pointer border bg-blue-700 ${dominoTheme === 'blue' ? 'border-yellow-400 scale-105' : 'border-gray-500'}`}></div>
                </div>
              </div>
              <div className="mb-4 border-t border-gray-700 pt-2">
                <label className="text-gray-300 text-[10px] font-bold block mb-2 text-center">{texts.styleLabel}</label>
                <div className="grid grid-cols-2 gap-2 text-[10px] font-bold">
                  <button onClick={() => setDominoStyle('flat')} className={`py-2 rounded border flex flex-col items-center gap-1 ${dominoStyle === 'flat' ? 'bg-yellow-500 text-black' : 'bg-[#152a21] text-gray-300'}`}>PLAT</button>
                  <button onClick={() => setDominoStyle('bevel')} className={`py-2 rounded border flex flex-col items-center gap-1 ${dominoStyle === 'bevel' ? 'bg-yellow-500 text-black' : 'bg-[#152a21] text-gray-300'}`}>3D</button>
                </div>
              </div>
              <button onClick={() => setShowSettings(false)} className="w-full bg-yellow-600 hover:bg-yellow-500 text-black font-bold py-2 rounded text-xs">TERMINER</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ----- IN-GAME -----
  const isMyTurn = turn === 'player' && status === 'playing' && !isDistributing;
  const playerHasMove = hasAnyValidMove(playerHand, board);
  const canDraw = !playerHasMove && boneyard.length > 0;
  const canPass = !playerHasMove && boneyard.length === 0;
  const opponentName = gameMode === 'pvp' ? (pvpOpponent?.username || 'P2') : (lang === 'EN' ? 'AI' : 'IA');
  const opponentAvatar = gameMode === 'pvp' ? (pvpOpponent?.avatar || '🐆') : '🤖';

  return (
    <div className="h-[100dvh] flex flex-col bg-[#05110d] text-white overflow-hidden font-sans select-none relative" data-testid="domino-ingame">
      <div className="flex-none bg-[#0a1a14] border-b border-[#d4af37]/20 p-2 sm:p-3 flex justify-between items-center z-20 shadow-md relative">
        <div className={`flex items-center space-x-2 p-1.5 rounded-full pr-3 sm:pr-4 transition-colors ${turn === 'player' ? 'bg-[#d4af37]/20 border border-[#d4af37] shadow-[0_0_10px_rgba(212,175,55,0.3)]' : 'bg-[#152a21] border border-transparent'}`}>
          <div className="text-xl sm:text-2xl bg-gray-800 rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center border border-[#d4af37]/50">{myAvatar.icon}</div>
          <div className="flex flex-col">
            <span className="text-xs sm:text-sm font-bold text-[#d4af37]">{lang === 'FR' ? 'Vous' : lang === 'EN' ? 'You' : 'Tú'}</span>
            <span className="text-[10px] sm:text-xs text-gray-400">{isDistributing ? '?' : playerHand.length} {lang === 'FR' ? 'Pièces' : lang === 'EN' ? 'Tiles' : 'Fichas'}</span>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center gap-1 z-30">
          <div className="bg-gradient-to-r from-yellow-900 to-yellow-800 border border-yellow-500 rounded px-3 py-1 shadow-md flex flex-col items-center justify-center min-w-[80px] sm:min-w-[100px]">
            <span className="text-[9px] sm:text-[10px] text-yellow-400/80 font-bold uppercase leading-none">{texts.potLabel}</span>
            <span className="text-xs sm:text-sm font-black text-[#d4af37] drop-shadow-md leading-none mt-1" data-testid="domino-pot">{Math.round(gamePot)} DOMIA</span>
          </div>
          <div className={`flex items-center gap-1 bg-black/60 px-3 py-0.5 rounded-full border ${timeLeft <= 30 && status === 'playing' ? 'border-red-500 bg-red-900/30' : 'border-gray-600'}`}>
            <span className="text-xs sm:text-sm">⏱️</span>
            <span className={`font-mono font-bold text-xs sm:text-sm ${timeLeft <= 30 && status === 'playing' ? 'text-red-500 animate-pulse' : 'text-yellow-500'}`}>{timeLeft}s</span>
          </div>
        </div>
        <div className={`flex items-center space-x-2 p-1.5 rounded-full pl-3 sm:pl-4 flex-row-reverse transition-colors ${turn === 'ai' ? 'bg-[#d4af37]/20 border border-[#d4af37] shadow-[0_0_10px_rgba(212,175,55,0.3)]' : 'bg-[#152a21] border border-transparent'}`}>
          <div className="text-xl sm:text-2xl bg-gray-800 rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center border border-blue-500/50">{opponentAvatar}</div>
          <div className="flex flex-col items-end mr-2">
            <span className="text-xs sm:text-sm font-bold text-[#d4af37]">{opponentName}</span>
            <span className="text-[10px] sm:text-xs text-gray-400">{isDistributing ? '?' : aiHand.length} {lang === 'FR' ? 'Pièces' : 'Tiles'}</span>
          </div>
        </div>
      </div>

      <div className="flex-grow relative overflow-hidden flex flex-col">
        {activeMessage && (
          <div className="absolute top-10 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center animate-dropIn pointer-events-none w-[80%] sm:w-auto">
            <div className={`px-4 sm:px-6 py-2 sm:py-3 rounded-2xl rounded-bl-none font-bold shadow-[0_10px_25px_rgba(0,0,0,0.5)] border-2 min-w-[150px] sm:min-w-[200px] text-center relative ${activeMessage.sender === myAvatar.name ? 'bg-[#0f212e] text-white border-[#d4af37]' : 'bg-white text-black border-blue-500'}`}>
              <span className="text-[10px] sm:text-xs opacity-70 block mb-1">{activeMessage.sender} dit :</span>
              <span className="text-base sm:text-xl">{activeMessage.text}</span>
            </div>
          </div>
        )}

        <div className="absolute inset-0 bg-[#0a3a1f] overflow-hidden flex flex-col items-center justify-center">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: "url('https://customer-assets.emergentagent.com/job_palette-preview-12/artifacts/sa1hjphq_file_000000006e4071f6949977515f431eb3.png')",
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
            }}
          ></div>
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at center, transparent 30%, rgba(0,0,0,0.55) 100%)' }}></div>
        </div>

        {!isDistributing && aiHand.length > 0 && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 z-20 flex justify-center gap-1.5 opacity-90 max-w-[90%] scale-[0.85] sm:scale-100">
            {aiHand.map((_, idx) => (
              <div key={idx} className="w-8 h-16 bg-gradient-to-br from-[#3e2b05] to-[#1e1300] border-2 border-yellow-600/70 rounded-md shadow-[2px_3px_5px_rgba(0,0,0,0.6)] flex items-center justify-center -ml-2 first:ml-0 pointer-events-none">
                <div className="w-1.5 h-1.5 bg-yellow-600/40 rounded-full animate-pulse"></div>
              </div>
            ))}
          </div>
        )}

        {!isDistributing && (
          <div className="absolute top-4 left-4 z-20 bg-black/60 border border-yellow-700/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg backdrop-blur-sm animate-fadeIn">
            <span className="text-gray-300 text-xs sm:text-sm font-bold">{texts.piocheLabel} <span className="text-[#d4af37] text-base sm:text-lg">{boneyard.length}</span></span>
          </div>
        )}

        {!isDistributing && (
          <button onClick={() => setShowQuitConfirm(true)} className="absolute top-4 right-4 z-20 bg-red-950/80 hover:bg-red-900 border border-red-600/60 text-red-200 px-3 py-1.5 text-xs font-bold rounded-lg backdrop-blur-sm transition-all shadow-md active:scale-95 flex items-center gap-1">
            🚪 <span className="hidden sm:inline">{texts.quitText}</span>
          </button>
        )}

        {isDistributing && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-50 bg-black/40 backdrop-blur-[2px] animate-fadeInOut">
            <div className="relative w-32 h-32 flex items-center justify-center">
              <DominoTile tile={{ v1: 0, v2: 0 }} hidden={true} theme={dominoTheme} dominoStyle={dominoStyle} className="absolute animate-spin shadow-2xl z-30" />
              <DominoTile tile={{ v1: 0, v2: 0 }} hidden={true} theme={dominoTheme} dominoStyle={dominoStyle} className="absolute animate-[spin_2s_reverse_infinite] -translate-x-6 translate-y-4 shadow-xl z-20 opacity-80" />
              <DominoTile tile={{ v1: 0, v2: 0 }} hidden={true} theme={dominoTheme} dominoStyle={dominoStyle} className="absolute animate-[ping_1.5s_infinite] translate-x-6 -translate-y-4 shadow-xl z-10 opacity-60" />
            </div>
            <h2 className="text-[#d4af37] text-xl sm:text-2xl font-bold mt-12 animate-pulse drop-shadow-md text-center px-4">{lang === 'EN' ? 'Shuffling cards...' : 'Distribution des cartes...'}</h2>
          </div>
        )}

        {board.length === 0 && !isDistributing && (
          <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none p-4 mt-8">
            <div className="text-[#d4af37]/80 text-sm sm:text-xl font-bold p-6 sm:p-8 text-center border-2 border-dashed border-[#d4af37]/30 rounded-xl bg-black/50 backdrop-blur-sm shadow-xl animate-dropIn whitespace-nowrap">
              {texts.platVide}<br />{isMyTurn ? (lang === 'FR' ? "Jouez !" : lang === 'EN' ? "Play !" : "¡Juega!") : (lang === 'FR' ? "L'adv. réfléchit..." : lang === 'EN' ? "Opponent thinking..." : "El oponente piensa...")}
            </div>
          </div>
        )}

        {!isDistributing && board.length > 0 && (
          <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none mt-8">
            <div className="relative w-full h-full transition-transform duration-500 ease-in-out origin-center pointer-events-auto" style={{ transform: `scale(${zoom})` }}>
              {boardLayout.map((item, idx) => (
                <DominoTile key={idx} tile={item.displayTile} isBoard={true} theme={dominoTheme} dominoStyle={dominoStyle}
                  style={{ left: '50%', top: '50%', transform: `translate(calc(-50% + ${item.x}px), calc(-50% + ${item.y}px)) rotate(${item.rot}deg)`, zIndex: idx }} />
              ))}
            </div>
          </div>
        )}

        {status === 'finished' && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto pt-6">
            <div className="bg-[#0f212e] p-5 rounded-2xl border-2 border-[#d4af37] text-center w-[95%] max-w-sm mx-auto animate-dropIn shadow-[0_0_50px_rgba(212,175,55,0.3)] pointer-events-auto my-auto" data-testid="domino-result">
              <h2 className="text-xl sm:text-3xl font-black text-[#d4af37] mb-1 tracking-widest leading-tight">{winReason === 'timeout' ? texts.timeout : finalScores ? (winner === 'tie' ? 'ÉGALITÉ' : texts.blocage) : 'BENEDICAMUS'}</h2>
              <h3 className="text-sm font-bold text-white mb-3 tracking-wider">{winReason === 'timeout' ? (lang === 'FR' ? 'Match terminé' : lang === 'EN' ? 'Match Ended' : 'Partido Terminado') : finalScores ? texts.gameBlocked : 'DOMINO !'}</h3>
              <div className="text-xs sm:text-sm mb-4 font-medium">
                {winReason === 'timeout' ? (
                  winner === 'player' ? (lang === 'FR' ? "🎉 L'adv. a mis trop de temps ! Vous gagnez." : "🎉 Opponent ran out of time! You win.") : (lang === 'FR' ? "🤖 Vous avez mis trop de temps ! L'adv. gagne." : "🤖 You ran out of time! Opponent wins.")
                ) : finalScores ? (
                  <div className="bg-black/40 rounded-xl p-3 border border-[#d4af37]/30">
                    <p className={`text-xs mb-1 ${winner === 'player' ? 'text-green-400 font-bold' : 'text-gray-300'}`}>{texts.winPts} <span className="text-white text-sm">{finalScores.player}</span></p>
                    <p className={`text-xs mb-2 ${winner === 'ai' ? 'text-red-400 font-bold' : 'text-gray-300'}`}>{texts.aiPts} <span className="text-white text-sm">{finalScores.ai}</span></p>
                    <div className="text-yellow-500 font-black border-t border-gray-600 pt-2 text-xs">{winner === 'tie' ? (lang === 'FR' ? "MATCH NUL !" : "DRAW!") : winner === 'player' ? (lang === 'FR' ? "🎉 VOUS GAGNEZ !" : "🎉 YOU WIN!") : (lang === 'FR' ? "🤖 L'ADVERSAIRE GAGNE !" : "🤖 OPPONENT WINS!")}</div>
                  </div>
                ) : (winner === 'player' ? texts.winnerNormal : texts.winnerAi)}
              </div>
              {winner === 'tie' && (
                <div className="bg-blue-900/40 text-blue-300 border border-blue-500/50 p-2 rounded text-xs mb-3 font-bold animate-pulse">
                  {lang === 'FR' ? '⚡ Nouvelle partie automatique dans 4s...' : lang === 'EN' ? '⚡ Auto-rematch in 4s...' : '⚡ Auto-revancha en 4s...'}
                </div>
              )}
              {gamePot > 0 && winner === 'player' && (
                <div className="bg-green-900/40 text-green-400 border border-green-500/50 p-1.5 rounded text-xs mb-3 font-bold">+{Math.round(gamePot)} DOMIA</div>
              )}
              <button onClick={() => { setStatus('start'); if (gameMode === 'pvp') { setGameMode('ai'); } }} className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-bold py-2 rounded shadow-lg text-xs uppercase" data-testid="domino-return-lobby">{texts.btnReturn}</button>
            </div>
          </div>
        )}

        <div className="absolute bottom-4 left-4 z-40 flex flex-col items-start pointer-events-auto">
          {isChatOpen && (
            <div className="bg-[#0f212e] border-2 border-[#d4af37] rounded-lg p-2 shadow-2xl mb-2 flex flex-col space-y-1 animate-dropIn min-w-[140px] sm:min-w-[160px]">
              <div className="text-[#d4af37] text-[10px] sm:text-xs font-bold px-2 pb-1 mb-1 border-b border-[#d4af37]/30">{texts.msgRapide}</div>
              {(lang === 'EN' ? QUICK_MESSAGES_EN : lang === 'ES' ? QUICK_MESSAGES_ES : QUICK_MESSAGES_FR).map((msg, i) => (
                <button key={i} onClick={() => sendPlayerMessage(msg)} className="text-left text-white text-xs sm:text-sm hover:bg-[#1a3a2a] hover:text-[#d4af37] px-3 py-2 rounded transition-colors">{msg}</button>
              ))}
            </div>
          )}
          <button onClick={() => setIsChatOpen(!isChatOpen)} className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-xl sm:text-2xl shadow-xl transition-all border-2 ${isChatOpen ? 'bg-[#d4af37] text-black border-white' : 'bg-[#152a21] text-white border-[#d4af37] hover:bg-[#2a4a3a]'}`}>💬</button>
        </div>

        <div className="absolute bottom-4 right-4 z-30 flex space-x-2 pointer-events-auto">
          <button onClick={() => setZoom(z => Math.max(0.2, z - 0.1))} className="bg-[#152a21] text-white border border-[#d4af37]/50 w-10 h-10 sm:w-12 sm:h-12 rounded-full font-bold shadow-lg flex items-center justify-center hover:bg-gray-700">➖</button>
          <button onClick={() => setZoom(z => Math.min(1.5, z + 0.1))} className="bg-[#152a21] text-white border border-[#d4af37]/50 w-10 h-10 sm:w-12 sm:h-12 rounded-full font-bold shadow-lg flex items-center justify-center hover:bg-gray-700">➕</button>
        </div>
      </div>

      {showQuitConfirm && (
        <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto pt-6">
          <div className="bg-[#0f212e] p-5 rounded-2xl border-2 border-red-500 text-center w-full max-w-sm shadow-[0_0_50px_rgba(239,68,68,0.4)] animate-dropIn my-auto">
            <div className="text-3xl mb-2">⚠️</div>
            <h2 className="text-lg font-black text-red-500 mb-2 uppercase tracking-wider">{lang === 'FR' ? "Confirmer l'abandon" : lang === 'EN' ? "Confirm Quit" : "Confirmar Salida"}</h2>
            <p className="text-gray-200 text-xs mb-5 leading-relaxed">{texts.quitConfirmDesc}</p>
            <div className="flex gap-2">
              <button onClick={() => setShowQuitConfirm(false)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 rounded text-xs uppercase">{texts.btnCancel}</button>
              <button onClick={() => {
                setShowQuitConfirm(false);
                if (gameMode === 'pvp' && socketRef.current && pvpOpponent) {
                  socketRef.current.emit('game_end', { winner: pvpOpponent.username, reason: 'quit' });
                  socketRef.current.emit('room_leave', {});
                }
                setStatus('start');
                if (gameMode === 'pvp') setGameMode('ai');
              }} className="flex-1 bg-gradient-to-r from-red-600 to-red-500 text-white font-bold py-2 rounded shadow-lg hover:scale-105 active:scale-95 text-xs uppercase" data-testid="domino-confirm-quit">{lang === 'FR' ? "OUI, QUITTER" : "YES, QUIT"}</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-none bg-[#0a1a14] p-2 sm:p-4 pb-10 sm:pb-8 border-t border-[#d4af37]/30 z-40 relative flex items-center justify-between shadow-[0_-10px_20px_rgba(0,0,0,0.5)] pointer-events-auto">
        {pendingMove !== null && !isDistributing && (
          <div className="absolute -top-14 left-1/2 -translate-x-1/2 z-50 flex space-x-3 bg-[#0a1a14] p-2 rounded-t-xl border-t border-l border-r border-[#d4af37]/50 shadow-[0_-10px_20px_rgba(0,0,0,0.6)] animate-dropIn">
            <button onClick={() => executePlayTile(pendingMove, 'left')} className="px-4 py-2 sm:px-5 sm:py-2.5 bg-blue-700 text-white font-bold text-[10px] sm:text-xs rounded shadow-lg hover:bg-blue-600 active:scale-95 transition-all border border-blue-400">👈 {lang === 'FR' ? 'GAUCHE' : 'LEFT'}</button>
            <button onClick={() => executePlayTile(pendingMove, 'right')} className="px-4 py-2 sm:px-5 sm:py-2.5 bg-red-700 text-white font-bold text-[10px] sm:text-xs rounded shadow-lg hover:bg-red-600 active:scale-95 transition-all border border-red-400">{lang === 'FR' ? 'DROITE' : 'RIGHT'} 👉</button>
          </div>
        )}

        <div className="hidden sm:flex flex-col items-center mr-2 flex-shrink-0">
          <div className="text-3xl bg-gray-700 rounded-full w-12 h-12 flex items-center justify-center border-2 border-[#d4af37]">{myAvatar.icon}</div>
          <span className="text-[#d4af37] font-bold text-xs mt-1">{lang === 'FR' ? 'Vous' : 'You'}</span>
        </div>

        <div className="flex-grow flex justify-start overflow-x-auto no-scrollbar space-x-2 sm:space-x-3 py-2 sm:py-4 px-2">
          {playerHand.map((tile, idx) => {
            const valid = isMyTurn && checkValidMove(tile, getEnds(board).left, getEnds(board).right);
            return (
              <div key={idx} className={`relative flex-shrink-0 ${isDistributing ? 'animate-slideUp' : ''}`} style={isDistributing ? { animationDelay: `${idx * 0.15}s`, opacity: 0, animationFillMode: 'forwards' } : {}}>
                <DominoTile tile={tile} isBoard={false} hidden={isDistributing} theme={dominoTheme} dominoStyle={dominoStyle}
                  className={`transition-all duration-200 ${valid && !isDistributing ? 'hover:-translate-y-3 sm:hover:-translate-y-4 hover:shadow-[0_0_20px_rgba(212,175,55,0.8)] z-10' : ''} ${pendingMove === idx ? 'ring-2 sm:ring-4 ring-[#d4af37] -translate-y-4 sm:-translate-y-6 z-20 shadow-[0_0_25px_rgba(212,175,55,0.9)]' : ''} ${!valid && isMyTurn ? 'opacity-40 grayscale-[40%] pointer-events-none' : ''}`}
                  onClick={() => valid && attemptPlayTile(idx)} />
              </div>
            );
          })}
        </div>

        <div className="flex flex-col gap-1.5 sm:gap-2 ml-2 sm:ml-4 flex-shrink-0">
          <button onClick={handleDraw} disabled={!isMyTurn || !canDraw} className={`font-bold py-2 sm:py-3 px-3 sm:px-6 rounded-lg text-xs sm:text-sm transition-colors border ${!isMyTurn || !canDraw ? 'bg-gray-800 text-gray-600 border-gray-700 pointer-events-none' : 'bg-green-800 hover:bg-green-700 text-white border-green-500 shadow-[0_0_10px_rgba(34,197,94,0.3)]'}`} data-testid="domino-draw-btn">{lang === 'FR' ? 'PIOCHER' : lang === 'EN' ? 'DRAW' : 'ROBAR'}</button>
          <button onClick={handlePass} disabled={!isMyTurn || !canPass} className={`font-bold py-2 sm:py-3 px-3 sm:px-6 rounded-lg text-xs sm:text-sm transition-colors border ${!isMyTurn || !canPass ? 'bg-gray-800 text-gray-600 border-gray-700 pointer-events-none' : 'bg-yellow-700 hover:bg-yellow-600 text-white border-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.3)]'}`} data-testid="domino-pass-btn">{lang === 'FR' ? 'PASSER' : lang === 'EN' ? 'PASS' : 'PASAR'}</button>
        </div>
      </div>
    </div>
  );
}
